import { useMemo } from "react";
import { useGetAllConnectedQuery } from "../features/connection/connectionApiSlice";
import useRightSectionState from "./useRightSectionState";
import {
  getMessengerProfileTypeKey,
  getMessengerProfileTypeLabel,
} from "../utils/messengerProfiles";

const useMessengerProfileTabs = ({
  ProfileSelected,
  initialProfile,
  focusProfile = false,
  pageMode = false,
  name,
  users,
}) => {
  const profileState = useRightSectionState(ProfileSelected, initialProfile);
  const {
    profiles,
    selectedProfile,
    handleSelect,
    isLoading: profilesLoading,
    isError: profilesError,
    refetchProfiles,
  } = profileState;

  const userShops = useMemo(() => {
    const raw = profiles.filter(
      (profile) => !profile.isGeneralPublic && !profile.isMarketer,
    );
    const seen = new Set();
    return raw.filter((profile) => {
      const id = String(
        profile.shopid ||
          profile.shopId ||
          profile.id ||
          profile._id ||
          profile.name ||
          "",
      ).trim();
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [profiles]);

  const profileTabs = useMemo(() => {
    const tabs = [];
    const seen = new Set();

    userShops.forEach((shop) => {
      const typeKey = getMessengerProfileTypeKey(shop);
      if (seen.has(typeKey)) return;

      seen.add(typeKey);
      tabs.push({
        typeKey,
        label: getMessengerProfileTypeLabel(shop),
        primaryShop: shop,
        shops: userShops.filter(
          (profile) => getMessengerProfileTypeKey(profile) === typeKey,
        ),
      });
    });

    return tabs;
  }, [userShops]);

  const isUserActive = Boolean(
    !selectedProfile || selectedProfile.isGeneralPublic,
  );
  const isMarketerActive = Boolean(
    selectedProfile?.isMarketer && !selectedProfile.isGeneralPublic,
  );
  const shopFocusMode = Boolean(
    focusProfile ||
    (pageMode && selectedProfile && !isUserActive && !isMarketerActive),
  );
  const activeChatsUnreadCount = users.reduce(
    (total, chat) => total + (Number(chat.unreadCount) || 0),
    0,
  );
  const activeProfileShops = useMemo(() => {
    if (isUserActive || isMarketerActive) return [];
    if (shopFocusMode && selectedProfile) return [selectedProfile];
    if (!selectedProfile) return userShops;

    const currentKey = getMessengerProfileTypeKey(selectedProfile);
    return userShops.filter(
      (shop) => getMessengerProfileTypeKey(shop) === currentKey,
    );
  }, [
    isMarketerActive,
    isUserActive,
    selectedProfile,
    shopFocusMode,
    userShops,
  ]);

  const activeTabType = isUserActive
    ? "user"
    : getMessengerProfileTypeKey(selectedProfile || userShops[0]);
  const activeTabLabel = isUserActive
    ? "User Profile"
    : getMessengerProfileTypeLabel(selectedProfile || userShops[0]);

  const selectedShopId = String(
    (focusProfile && initialProfile?.profileId) ||
      selectedProfile?.shopid ||
      selectedProfile?.shopId ||
      selectedProfile?.id ||
      selectedProfile?._id ||
      "",
  );
  const focusedShopProfile = useMemo(() => {
    const requestedId = String(initialProfile?.profileId || "");
    if (requestedId) {
      const match = profiles.find((profile) =>
        [
          profile.shopid,
          profile.shopId,
          profile.id,
          profile._id,
          profile.serviceid,
          profile.companyid,
        ]
          .filter(Boolean)
          .some((id) => String(id) === requestedId),
      );
      if (match) return match;
    }
    return selectedProfile?.isGeneralPublic ? null : selectedProfile;
  }, [initialProfile?.profileId, profiles, selectedProfile]);
  const connectionQueryArgs = isMarketerActive
    ? { userid: name?._id, isMarketerProfile: "true" }
    : { userid: name?._id };
  const {
    currentData: shopConnectionsResponse,
    isLoading: shopTabConnectionsLoading,
    isFetching: shopTabConnectionsFetching,
    error: shopTabConnectionsQueryError,
    refetch: refetchShopTabConnections,
  } = useGetAllConnectedQuery(connectionQueryArgs, {
    skip: !name?._id,
  });
  const connectionPayload = useMemo(
    () =>
      shopConnectionsResponse?.connectedBT
        ? shopConnectionsResponse
        : shopConnectionsResponse?.data || shopConnectionsResponse || {},
    [shopConnectionsResponse],
  );
  const idOf = (value) => {
    if (value == null || value === "") return "";
    const normalize = (id) => {
      const normalized = String(id || "").trim();
      return normalized === "null" || normalized === "undefined"
        ? ""
        : normalized;
    };
    if (typeof value === "object") {
      return normalize(value._id || value.id);
    }
    return normalize(value);
  };
  const activeShopName =
    selectedProfile?.shopname ||
    selectedProfile?.shopName ||
    selectedProfile?.displayName ||
    selectedProfile?.companyname ||
    selectedProfile?.name ||
    "";
  const shopTabConnections = useMemo(() => {
    const seen = new Set();
    const profilesFromGroups = (
      Array.isArray(connectionPayload?.connectedBT)
        ? connectionPayload.connectedBT
        : []
    ).flatMap((group) =>
      (group?.items || []).flatMap((item) => {
        if (!item || item.isPlaceholder) return [];

        const groupBusinessType = String(
          item.linkBusinessType || item.businessType || group.businessType || "",
        )
          .toLowerCase()
          .replace(/[\s_-]+/g, "");
        const itemShop = item.shopid;
        const shopid =
          idOf(itemShop) ||
          (item.position === "Shop" ||
          (groupBusinessType &&
            groupBusinessType !== "generalpublic" &&
            groupBusinessType !== "user")
            ? idOf(item._id) || idOf(item.id)
            : "");
        const userid = idOf(item.userid) || idOf(item.userId);
        const id = shopid || userid || idOf(item._id) || idOf(item.id);
        if (!id) return [];

        const key = `${shopid ? "shop" : "user"}:${id}`;
        if (seen.has(key)) return [];
        seen.add(key);

        const shopNameCandidates = [
          item.shopname,
          itemShop?.shopname,
          item.shopName,
          itemShop?.shopName,
          item.companyname,
          itemShop?.companyname,
          item.companyName,
          itemShop?.companyName,
          item.profileName,
          itemShop?.profileName,
          item.shopTitle,
          item.servicename,
          item.businessName,
          itemShop?.name,
        ];
        const shopName =
          shopNameCandidates.find(
            (v) =>
              v &&
              typeof v === "string" &&
              v.trim() !== "" &&
              v.trim().toUpperCase() !== "N/A",
          ) || null;
        const isGeneralPublic =
          groupBusinessType === "generalpublic" ||
          groupBusinessType === "user";
        const userNameCandidates = [
          item.fullname,
          item.displayName,
          item.name,
          item.shopname,
        ];
        const userName =
          userNameCandidates.find(
            (v) =>
              v &&
              typeof v === "string" &&
              v.trim() !== "" &&
              v.trim().toUpperCase() !== "N/A",
          ) || null;
        const name = shopid
          ? shopName || "Connected shop"
          : userName || "Connected profile";
        return [
          {
            ...item,
            id,
            _id: idOf(item._id) || id,
            shopid: shopid || null,
            userid: userid || null,
            name,
            shopname: item.shopname || name,
            tabLabel: isGeneralPublic ? "General Public" : name,
            position: shopid ? "Shop" : "User",
            businessType:
              item.linkBusinessType ||
              item.businessType ||
              group.businessType ||
              (shopid ? "shop" : "generalPublic"),
          },
        ];
      }),
    );
    seen.clear();

    const currentShopIds = new Set(
      [
        selectedProfile?.shopid,
        selectedProfile?.shopId,
        selectedProfile?.id,
        selectedProfile?._id,
        selectedProfile?.serviceid,
        selectedProfile?.companyid,
        focusProfile ? initialProfile?.profileId : null,
      ]
        .map(idOf)
        .filter(Boolean),
    );
    const profilesFromRows = (
      Array.isArray(connectionPayload?.connected)
        ? connectionPayload.connected
        : []
    ).flatMap((row) => {
      const senderId = idOf(row?.senderid);
      const receiverId = idOf(row?.recieverid);
      const senderShopId = idOf(row?.sendershopid);
      const receiverShopId = idOf(row?.recievershopid);
      const senderContextId = idOf(row?.senderShopContextId);
      const receiverContextId = idOf(row?.recieverShopContextId);
      const ownerId = idOf(name?._id);
      const ownerIsSender = Boolean(ownerId && senderId === ownerId);
      const ownerIsReceiver = Boolean(ownerId && receiverId === ownerId);
      const senderIsCurrentShop =
        currentShopIds.has(senderShopId) ||
        currentShopIds.has(senderContextId) ||
        (!senderShopId && currentShopIds.has(senderId));
      const receiverIsCurrentShop =
        currentShopIds.has(receiverShopId) ||
        currentShopIds.has(receiverContextId) ||
        (!receiverShopId && currentShopIds.has(receiverId));
      let otherIsSender;
      if (senderIsCurrentShop !== receiverIsCurrentShop) {
        otherIsSender = receiverIsCurrentShop;
      } else {
        // The endpoint already scopes these rows to the requested shop. When
        // its public route ID cannot be matched to a stored context ID, use
        // the authenticated shop owner's participant side as the fallback.
        if (ownerIsSender === ownerIsReceiver) return [];
        otherIsSender = ownerIsReceiver;
      }

      const otherUserId = otherIsSender ? senderId : receiverId;
      const otherShopId = otherIsSender ? senderShopId : receiverShopId;
      const otherContextId = otherIsSender
        ? senderContextId
        : receiverContextId;
      const profileId = otherShopId || otherUserId;
      if (!profileId) return [];

      const key = `${otherShopId ? "shop" : "user"}:${profileId}`;
      if (seen.has(key)) return [];
      seen.add(key);

      const otherUser = otherIsSender ? row?.senderUser : row?.receiverUser;
      const otherShopIdValue = otherIsSender
        ? row?.sendershopid || row?.senderShopContextId
        : row?.recievershopid || row?.recieverShopContextId;
      const otherShop =
        otherShopIdValue && typeof otherShopIdValue === "object"
          ? otherShopIdValue
          : null;
      const shopNameCandidates = otherIsSender
        ? [
            row?.sendershopname,
            row?.senderShopName,
            row?.senderShopContextName,
            otherShop?.shopname,
            otherShop?.shopName,
            otherShop?.profileName,
            otherShop?.companyname,
            otherShop?.companyName,
            otherShop?.shopTitle,
            otherShop?.servicename,
            otherShop?.businessName,
            otherShop?.name,
            otherShop?.displayName,
          ]
        : [
            row?.recievershopname,
            row?.recieverShopName,
            row?.recieverShopContextName,
            otherShop?.shopname,
            otherShop?.shopName,
            otherShop?.profileName,
            otherShop?.companyname,
            otherShop?.companyName,
            otherShop?.shopTitle,
            otherShop?.servicename,
            otherShop?.businessName,
            otherShop?.name,
            otherShop?.displayName,
          ];
      const shopName =
        shopNameCandidates.find(
          (v) =>
            v &&
            typeof v === "string" &&
            v.trim() !== "" &&
            v.trim().toUpperCase() !== "N/A",
        ) || null;
      const userNameCandidates = [
        otherUser?.fullname,
        otherUser?.name,
        otherUser?.shopname,
      ];
      const userName =
        userNameCandidates.find(
          (v) =>
            v &&
            typeof v === "string" &&
            v.trim() !== "" &&
            v.trim().toUpperCase() !== "N/A",
        ) || null;
      const profileName = otherShopId
        ? shopName || "Connected shop"
        : userName || "Connected profile";
      const connectedViaShopNameCandidates = otherIsSender
        ? [
            row?.recievershopname,
            row?.recieverShopContextName,
            activeShopName,
          ]
        : [
            row?.sendershopname,
            row?.senderShopContextName,
            activeShopName,
          ];
      const connectedViaShopName =
        connectedViaShopNameCandidates.find(
          (v) =>
            v &&
            typeof v === "string" &&
            v.trim() !== "" &&
            v.trim().toUpperCase() !== "N/A",
        ) || null;
      const rawRowBT =
        (otherIsSender ? row?.senderBusinessType : row?.businessType) ||
        row?.linkBusinessType ||
        row?.businessType ||
        row?.senderBusinessType ||
        otherShop?.businessType ||
        otherShop?.linkBusinessType ||
        row?.category ||
        "";
      const resolvedBT = rawRowBT
        ? getMessengerProfileTypeKey({ businessType: rawRowBT }) || rawRowBT
        : otherShopId
          ? "shop"
          : "generalPublic";
      return [
        {
          _id: profileId,
          id: profileId,
          userid: otherUserId || null,
          shopid: otherShopId || null,
          shopContextId: otherContextId || null,
          name: profileName || "Connected profile",
          shopname: profileName || "Connected profile",
          tabLabel: otherShopId
            ? shopName || "Connected shop"
            : connectedViaShopName || "General Public",
          connectedViaShopName: otherShopId
            ? null
            : connectedViaShopName || null,
          profilePic: otherIsSender
            ? row?.sendershopimage || otherUser?.imageUrl || otherUser?.image
            : row?.recievershopimage || otherUser?.imageUrl || otherUser?.image,
          businessType: resolvedBT,
          position: otherShopId ? "Shop" : "User",
        },
      ];
    });

    const groupProfileKeys = new Set(
      profilesFromGroups.map(
        (profile) =>
          `${profile.shopid ? "shop" : "user"}:${idOf(
            profile.shopid || profile.userid || profile.id,
          )}`,
      ),
    );
    const rowProfilesWithoutGroups = profilesFromRows.filter((profile) => {
      const key = `${profile.shopid ? "shop" : "user"}:${idOf(
        profile.shopid || profile.userid || profile.id,
      )}`;
      return !groupProfileKeys.has(key);
    });
    const rowProfilesByKey = new Map(
      profilesFromRows.map((profile) => [
        `${profile.shopid ? "shop" : "user"}:${idOf(
          profile.shopid || profile.userid || profile.id,
        )}`,
        profile,
      ]),
    );
    const enrichedGroupProfiles = profilesFromGroups.map((profile) => {
      if (profile.shopid) return profile;

      const key = `user:${idOf(profile.userid || profile.id)}`;
      const rowProfile = rowProfilesByKey.get(key);
      if (!rowProfile?.connectedViaShopName) return profile;

      return {
        ...profile,
        tabLabel: "General Public",
        connectedViaShopName: rowProfile.connectedViaShopName,
      };
    });

    const allConnections = [
      ...enrichedGroupProfiles,
      ...rowProfilesWithoutGroups,
    ];
    return allConnections;
  }, [
    connectionPayload,
    focusProfile,
    initialProfile?.profileId,
    name?._id,
    selectedProfile,
    activeShopName,
    isUserActive,
  ]);
  const shopConnectionErrorDetail =
    shopTabConnectionsQueryError?.data?.error?.message ||
    shopTabConnectionsQueryError?.data?.message ||
    shopTabConnectionsQueryError?.error ||
    "";
  const shopTabConnectionsError = shopTabConnectionsQueryError
    ? `Unable to load ${
        isUserActive ? "General Public" : "this shop's"
      } connections${
        shopTabConnectionsQueryError.status
          ? ` (HTTP ${shopTabConnectionsQueryError.status})`
          : ""
      }${shopConnectionErrorDetail ? `: ${shopConnectionErrorDetail}` : "."}`
    : "";
  const shopTabConnectionSourceCount =
    (Array.isArray(connectionPayload?.connected)
      ? connectionPayload.connected.length
      : 0) +
    (Array.isArray(connectionPayload?.connectedBT)
      ? connectionPayload.connectedBT.reduce(
          (total, group) =>
            total +
            (Array.isArray(group?.items)
              ? group.items.filter((item) => !item?.isPlaceholder).length
              : 0),
          0,
        )
      : 0);

  return {
    ...profileState,
    activeChatsUnreadCount,
    activeProfileShops,
    activeTabLabel,
    activeTabType,
    isMarketerActive,
    isUserActive,
    shopFocusMode,
    profileTabs,
    profilesLoading,
    profilesError,
    refetchProfiles,
    shopTabConnections,
    focusedShopProfile,
    shopTabConnectionsError,
    refetchShopTabConnections,
    shopTabConnectionSourceCount,
    shopTabConnectionsLoading:
      shopTabConnectionsLoading || shopTabConnectionsFetching,
    userShops,
  };
};

export default useMessengerProfileTabs;
